/**
 * Environmental Geotech — Anomaly Alerts  (Steps 3 + 4 of the 8-step intelligence loop)
 *
 * Step 3: AI-generated probable-cause narrative for satellite-detected anomalies on
 *         protected zones (deforestation, illegal clearing, wetland encroachment,
 *         illegal mining, wildfire, landslide).
 * Step 4: Human verification gate — Confirm → Schedule Canopy Drone Survey /
 *         Confirm → Notify Authority / Reject (false positive) / Defer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, CheckCircle2, XCircle, Clock, Zap,
  TreePine, ShieldAlert,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity      = 'low' | 'medium' | 'high' | 'critical';
type AnomalyStatus = 'pending' | 'investigating' | 'resolved' | 'false_positive';

interface EnvAnomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: Severity;
  status: AnomalyStatus;
  change_percentage: number | string;
  affected_area_hectares?: number;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
}

// ─── Step 4 decision options ──────────────────────────────────────────────────

const STEP4_ACTIONS = [
  {
    id: 'confirm_drone',
    label: 'Confirm — Schedule Canopy Drone Survey',
    icon: TreePine,
    colour: 'bg-green-600 text-white hover:bg-green-700',
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[STEP4: Confirmed → Canopy drone survey scheduled] ',
    badge: 'triggers Step 5',
    badgeClass: 'bg-green-100 text-green-700',
  },
  {
    id: 'confirm_authority',
    label: 'Confirm — Notify Environmental Authority',
    icon: ShieldAlert,
    colour: 'bg-red-600 text-white hover:bg-red-700',
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[STEP4: Confirmed → Authority notified] ',
    badge: 'enforcement action',
    badgeClass: 'bg-red-100 text-red-700',
  },
  {
    id: 'confirm_ground',
    label: 'Confirm — Ground Patrol Inspection Only',
    icon: CheckCircle2,
    colour: 'bg-blue-600 text-white hover:bg-blue-700',
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[STEP4: Confirmed → Ground patrol dispatched] ',
    badge: null,
    badgeClass: '',
  },
  {
    id: 'reject',
    label: 'Reject — False Positive',
    icon: XCircle,
    colour: 'bg-gray-600 text-white hover:bg-gray-700',
    newStatus: 'false_positive' as AnomalyStatus,
    notePrefix: '[STEP4: Rejected] ',
    badge: null,
    badgeClass: '',
  },
  {
    id: 'defer',
    label: 'Defer — Monitor Next Satellite Pass',
    icon: Clock,
    colour: 'bg-amber-500 text-white hover:bg-amber-600',
    newStatus: 'pending' as AnomalyStatus,
    notePrefix: '[STEP4: Deferred] ',
    badge: null,
    badgeClass: '',
  },
];

// ─── Style maps ───────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<Severity, string> = {
  low:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  medium:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  high:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  critical: 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-200',
};

const STATUS_STYLE: Record<AnomalyStatus, string> = {
  pending:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  investigating:  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  resolved:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  false_positive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  pending:        'Pending Review',
  investigating:  'Action Taken',
  resolved:       'Resolved',
  false_positive: 'False Positive',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EnvAnomalyAlertsPage: React.FC = () => {
  const [anomalies, setAnomalies]   = useState<EnvAnomaly[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [zones, setZones]           = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading]       = useState(true);
  const [monitoring, setMonitoring] = useState(false);
  const [monitoringZoneId, setMonitoringZoneId] = useState('');
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);

  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterZone,     setFilterZone]     = useState('');

  const [reviewingAnomaly, setReviewingAnomaly] = useState<EnvAnomaly | null>(null);
  const [actionId,         setActionId]         = useState('');
  const [actionNote,       setActionNote]       = useState('');
  const [submitting,       setSubmitting]       = useState(false);
  // Editable AI analysis fields
  const [editType,        setEditType]        = useState('');
  const [editChange,      setEditChange]      = useState('');
  const [editArea,        setEditArea]        = useState('');
  const [editDescription, setEditDescription] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, statsRes] = await Promise.all([
        anomalyApi.getAnomalies({
          severity:   filterSeverity || undefined,
          status:     filterStatus   || undefined,
          boundaryId: filterZone     || undefined,
          vertical:   'environmental',
        }),
        anomalyApi.getStats('environmental'),
      ]);
      setAnomalies(anomalyRes.anomalies ?? []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterStatus, filterZone]);

  useEffect(() => {
    load();
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all = res.data?.boundaries ?? [];
        setZones(all.filter((b: any) => b.metadata?.vertical === 'environmental'));
      })
      .catch(() => {});
  }, [load]);

  const handleRunMonitoring = async () => {
    setMonitoring(true); setError(null);
    try {
      if (monitoringZoneId) {
        await anomalyApi.detectChanges(monitoringZoneId);
        const selected = zones.find((z) => z.id === monitoringZoneId);
        setSuccess(`Monitoring complete for zone: ${selected?.name ?? monitoringZoneId}`);
      } else {
        const res = await anomalyApi.runMonitoring('environmental');
        setSuccess(`Monitoring complete — ${res.anomaliesDetected ?? 0} anomaly(s) detected`);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Monitoring failed');
    } finally {
      setMonitoring(false);
    }
  };

  const handleAction = async () => {
    if (!reviewingAnomaly || !actionId) return;
    const action = STEP4_ACTIONS.find((a) => a.id === actionId)!;
    setSubmitting(true);
    try {
      const corrections: string[] = [];
      if (editType        !== reviewingAnomaly.anomaly_type)                         corrections.push(`type: ${editType}`);
      if (editChange      !== String(reviewingAnomaly.change_percentage ?? ''))      corrections.push(`NDVI Δ: ${editChange}%`);
      if (editArea        !== String(reviewingAnomaly.affected_area_hectares ?? '')) corrections.push(`area: ${editArea} ha`);
      if (editDescription !== (reviewingAnomaly.description ?? ''))                  corrections.push(`description: ${editDescription}`);
      const correctionNote = corrections.length ? ` [analyst corrections — ${corrections.join('; ')}]` : '';
      await anomalyApi.updateStatus(reviewingAnomaly.id, {
        status: action.newStatus,
        notes:  `${action.notePrefix}${actionNote}${correctionNote}`.trim(),
      });
      setSuccess(`Decision recorded: ${action.label}`);
      setReviewingAnomaly(null);
      setActionId('');
      setActionNote('');
      setEditType(''); setEditChange(''); setEditArea(''); setEditDescription('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount  = anomalies.filter((a) => a.status === 'pending').length;
  const criticalCount = anomalies.filter((a) => a.severity === 'critical' && a.status === 'pending').length;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-500" /> Anomaly Alerts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            AI-detected environmental anomalies (Step 3) — review and verify each alert (Step 4)
          </p>
          {(pendingCount > 0 || criticalCount > 0) && (
            <div className="flex items-center gap-2 mt-2">
              {criticalCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-200 text-red-900">
                  <Zap className="w-3 h-3" /> {criticalCount} critical pending
                </span>
              )}
              {pendingCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {pendingCount} awaiting review
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={monitoringZoneId}
            onChange={(e) => setMonitoringZoneId(e.target.value)}
            className={SELECT_CLS}
            disabled={monitoring}
          >
            <option value="">All Zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <Button onClick={handleRunMonitoring} loading={monitoring} variant="outline">
            <Zap className="w-4 h-4 mr-1" /> Run Monitoring
          </Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total',          value: stats.total                ?? 0, col: 'text-gray-900 dark:text-white' },
            { label: 'Critical',       value: stats.bySeverity?.critical ?? 0, col: 'text-red-700 dark:text-red-400' },
            { label: 'High',           value: stats.bySeverity?.high     ?? 0, col: 'text-orange-700 dark:text-orange-400' },
            { label: 'Pending Review', value: stats.byStatus?.pending    ?? 0, col: 'text-amber-700 dark:text-amber-400' },
          ].map(({ label, value, col }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${col}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className={SELECT_CLS}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={SELECT_CLS}>
            <option value="">All Statuses</option>
            <option value="pending">Pending Review</option>
            <option value="investigating">Action Taken</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>
          <select value={filterZone} onChange={(e) => setFilterZone(e.target.value)} className={SELECT_CLS}>
            <option value="">All Zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <Button size="sm" onClick={load}>Apply</Button>
          {(filterSeverity || filterStatus || filterZone) && (
            <button onClick={() => { setFilterSeverity(''); setFilterStatus(''); setFilterZone(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline">Clear</button>
          )}
        </div>
      </Card>

      {/* ── Anomaly table ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {anomalies.length} anomaly{anomalies.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-400">Click <strong>Review</strong> to record your Step 4 decision</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : anomalies.length === 0 ? (
          <div className="text-center py-14">
            <AlertTriangle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No environmental anomalies found</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>Run Monitoring</strong> to detect satellite changes</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Zone', 'Type', 'Severity', 'NDVI Δ', 'Area (ha)', 'Detected', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {anomalies.map((a) => (
                  <tr key={a.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${a.severity === 'critical' && a.status === 'pending' ? 'bg-red-50/40 dark:bg-red-900/5' : ''}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{a.boundary_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{a.anomaly_type?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[a.severity]}`}>{a.severity}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {a.change_percentage != null ? (
                        <span className={Number(a.change_percentage) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                          {Number(a.change_percentage) > 0 ? '+' : ''}{Number(a.change_percentage).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {a.affected_area_hectares != null ? Number(a.affected_area_hectares).toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {a.detection_date ? new Date(a.detection_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                      {(a.status === 'investigating' || a.status === 'resolved') && a.resolution_notes && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={a.resolution_notes}>
                          {a.resolution_notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant={a.status === 'pending' ? 'primary' : 'outline'}
                        onClick={() => {
                          setReviewingAnomaly(a);
                          setActionId('');
                          setActionNote('');
                          setEditType(a.anomaly_type ?? '');
                          setEditChange(a.change_percentage != null ? String(a.change_percentage) : '');
                          setEditArea(a.affected_area_hectares != null ? String(a.affected_area_hectares) : '');
                          setEditDescription(a.description ?? '');
                        }}>
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Step 4 Decision Modal ── */}
      {reviewingAnomaly && (
        <Modal onClose={() => setReviewingAnomaly(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Step 4 — Human Verification</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{reviewingAnomaly.boundary_name}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[reviewingAnomaly.severity]}`}>
                {reviewingAnomaly.severity}
              </span>
            </div>

            {/* Previously recorded decision (if any) */}
            {(reviewingAnomaly.status === 'investigating' || reviewingAnomaly.status === 'resolved') && reviewingAnomaly.resolution_notes && (
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 mb-4 text-sm">
                <p className="font-semibold text-blue-700 dark:text-blue-300 mb-1">Action Recorded</p>
                <p className="text-blue-800 dark:text-blue-200 whitespace-pre-wrap">{reviewingAnomaly.resolution_notes}</p>
              </div>
            )}

            {/* AI Analysis — editable by analyst */}
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 mb-4 text-sm space-y-2">
              <p className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                AI Analysis
                <span className="text-[10px] font-normal text-gray-400 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">editable</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">Anomaly Type</label>
                  <input
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">NDVI Δ (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editChange}
                    onChange={(e) => setEditChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">Affected Area (ha)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editArea}
                    onChange={(e) => setEditArea(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">Description / Probable Cause</label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Analyst narrative or correction to AI-generated description…"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Action selection */}
            <div className="space-y-2 mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Your Decision</p>
              {STEP4_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActionId(action.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${actionId === action.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                    {action.label}
                    {action.badge && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${action.badgeClass}`}>{action.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
              <textarea rows={2} value={actionNote} onChange={(e) => setActionNote(e.target.value)}
                placeholder="Patrol contact, legal jurisdiction, regulatory authority reference…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleAction} disabled={!actionId || submitting} loading={submitting} className="flex-1">
                Record Decision
              </Button>
              <Button variant="outline" onClick={() => setReviewingAnomaly(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default EnvAnomalyAlertsPage;

// Made with Bob
